import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function CompanyContact() {
  const { ref, isVisible } = useScrollAnimation();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      // Prevent submission if the message exceeds the limit
      return;
    }

    setIsSubmitting(true);
    try {
      await fetch('https://readdy.ai/api/form/d6am0q5bd17lru29ltjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        // Convert FormData to URL‑encoded string safely
        body: new URLSearchParams(
          Object.fromEntries(formData.entries() as Iterable<[string, string]>)
        ).toString(),
      });
      setIsSubmitted(true);
    } catch (error) {
      // Log the error for debugging purposes
      console.error('Form submission failed:', error);
      setIsSubmitted(true); // Still show the thank‑you UI
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={ref} id="company-contact" className="bg-gray-50 py-32 lg:py-40 px-8 lg:px-32">
      <div className="max-w-3xl mx-auto">
        <div
          className={`mb-16 text-center transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-4xl lg:text-5xl font-medium text-gray-900 mb-3">Contact</h2>
          <p className="text-base text-gray-500 mb-6">お問い合わせ</p>
          <p className="text-sm text-gray-600 leading-relaxed">
            プロジェクトのご相談、Echoのレンタル、その他お問い合わせはこちらからお気軽にどうぞ。
          </p>
        </div>

        {isSubmitted ? (
          <div
            className={`text-center py-16 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-gray-900 rounded-full">
              <i className="ri-check-line text-2xl text-white" />
            </div>
            <h3 className="text-2xl font-medium text-gray-900 mb-4">送信完了</h3>
            <p className="text-sm text-gray-600 mb-2">お問い合わせありがとうございます。</p>
            <p className="text-sm text-gray-500">2営業日以内にご返信いたします。</p>
            <button
              type="button"
              onClick={() => setIsSubmitted(false)}
              className="mt-8 px-8 h-12 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              新しいお問い合わせ
            </button>
          </div>
        ) : (
          <form
            id="company-contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className={`space-y-6 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  お名前 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  className="w-full h-12 px-4 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-gray-400 transition-colors"
                  placeholder="山田 太郎"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレス <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full h-12 px-4 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-gray-400 transition-colors"
                  placeholder="example@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                会社名
              </label>
              <input
                type="text"
                name="company"
                className="w-full h-12 px-4 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-gray-400 transition-colors"
                placeholder="株式会社〇〇"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                お問い合わせ内容 <span className="text-red-400">*</span>
              </label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-gray-400 transition-colors resize-none"
                placeholder="お問い合わせ内容をご記入ください（500文字以内）"
              />
            </div>

            <div className="text-center pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-12 h-14 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-base w-4 h-4 flex items-center justify-center" />
                    送信中...
                  </span>
                ) : (
                  '送信する / Submit'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
